<script setup lang="ts">

import { computed, ref, onMounted } from 'vue';
import { useWindowSize } from '@vueuse/core';
import { usePresentation } from "../Types/Presentation";

import { MAX_IMAGE_HEIGHT_TO_PAGE_HEIGHT, RATIO } from '../Types/Settings';

type Props =
{ 
    src: string,
    border?: boolean,
    imageClass?: string,
    floorShadow?: boolean
}

const { src, floorShadow = false } = defineProps<Props>();

const { width, height } = useWindowSize();
const contentHeight = computed( () => width.value / RATIO )
const maxHeight = computed( () => width.value > height.value ? contentHeight.value * MAX_IMAGE_HEIGHT_TO_PAGE_HEIGHT : 1000 );

const animate = ref( false );
onMounted( () => setTimeout( () => { if( floorShadow == true ) animate.value = true } ))

const imageSrc = ref( src );
if( src.startsWith( '/images/' ) == false )
{
    const presentation = usePresentation();
    const key = presentation.currentSection?.isShared ? 'shared' : presentation.key;
    imageSrc.value = `/images/${key}/` + ( src.startsWith( '/' ) ? src.substring( 1 ) : src );
}

</script>

<template>

    <img 
        v-if="border == false && floorShadow == false"
        v-bind="$attrs"
        :src="imageSrc" 
        :class="imageClass"
    />

    <div v-else
         class="flex flex-col items-center w-full !max-w-[90%] md:!max-w-[70%] lg:!max-w-[90%] 2xl:!max-w-[800px] 2xl:mt-4">

        <div>
            <div class="transition-transform duration-1000"
                 :class="animate || floorShadow == false ? 'translate-y-0' : 'translate-y-6'">
                <img :src="imageSrc"
                    :key="String(animate)"
                    class="w-auto h-auto object-cover" 
                    :style="{ maxHeight: `${maxHeight}px`}"
                    :class="[ 
                        border ? 'rounded-md overflow-hidden shadow-2xl ring-4 ring-white' : '', 
                        imageClass 
                    ]" 
                />
            </div>

            <div v-if="floorShadow"
                 :class="animate ? 'opacity-100 scale-110 translate-y-7' : 'opacity-50 scale-75 translate-y-5'"
                 class="w-full h-8 absolute -bottom-10 transition-all duration-500 ease"
                 style="background-image: radial-gradient( ellipse farthest-side at center, rgba( 0,0,0, 0.2 ) 0%, transparent 100%);" 
            />

        </div>
    </div>

</template>

<style scoped lang="scss">

    .gradient-animation
    {
        background: linear-gradient(-45deg, #c43a4f, #9a62a6, #1faffc);
        background-size: 400% 400%;
        animation: gradient 15s ease infinite;
    }

    @keyframes gradient 
    {
        0% {
            background-position: 0% 50%;
        }
        50% {
            background-position: 100% 50%;
        }
        100% {
            background-position: 0% 50%;
        }
    }

</style>